import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, switchMap, tap } from 'rxjs';
import { GroupService } from './group.service';
import { PracticeSettingsStore } from './practice-settings-store.service';

@Injectable({
  providedIn: 'root'
})
export class ColleagueService {
  private baseUrl = 'api';
  private colleaguesSubject = new BehaviorSubject<any>(null);
  colleagues$ = this.colleaguesSubject.asObservable();
  groupId: any;

  constructor(private http: HttpClient, private groupService: GroupService, private settingsStore?: PracticeSettingsStore) {
    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          return this.getColleaguesByGroup(selectedGroup.id, true);
        }
        return EMPTY;
      })
    ).subscribe();
  }

  /** `fromKeptCopy`: on a practice switch, the device's kept copy while it is current (PracticeSettingsStore). */
  getColleaguesByGroup(groupId: string, fromKeptCopy = false): Observable<any> {
    const token = localStorage.getItem('token');
    const request = this.http.get(`${this.baseUrl}/colleagues/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const source = fromKeptCopy && this.settingsStore ? this.settingsStore.read(groupId, 'colleagues', () => request) : request;
    return source.pipe(
      tap(colleagues => {
        this.colleaguesSubject.next(colleagues);
      })
    );
  }

  /**
   * The theatre roster — `{ anaesthetists, surgicalAssistants }`.
   *
   * Server-filtered on `Colleague.isAnaesthetist` / `isSurgicalAssistant`, so the practice's whole
   * colleague network — mostly referrers, and much larger — never reaches the browser, and the
   * pickers cannot drift from the settings panel that populates them.
   *
   * Deliberately NOT fed through `colleaguesSubject`: that subject holds the full network for the
   * settings screens, and pushing a filtered two-list shape into it would silently truncate every
   * other consumer. This is a plain request/response, which is why the appointment screen
   * subscribes to it directly.
   *
   * Moved from the deleted `TheatreListService` when §3b removed the theatre-list surface; the
   * endpoint moved with it, from `/theatre-lists/roster/:groupId` to `/colleagues/roster/:groupId`.
   */
  getRoster(groupId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.baseUrl}/colleagues/roster/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  addColleague(colleague: any): Observable<any> {
    const token = localStorage.getItem('token');
    colleague.groupId = this.groupId;
    return this.http.post(`${this.baseUrl}/colleague`, { colleague }, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(() => this.getColleaguesByGroup(this.groupId))
    );
  }

  updateColleague(colleague: any): Observable<any> {
    const token = localStorage.getItem('token');
    colleague.groupId = this.groupId;
    return this.http.put(`${this.baseUrl}/colleague`, { colleague }, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(() => this.getColleaguesByGroup(this.groupId))
    );
  }

  deleteColleague(colleague: any): Observable<any> {
    const token = localStorage.getItem('token');
    colleague.groupId = this.groupId;
    return this.http.delete(`${this.baseUrl}/colleague/${colleague.id}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(() => this.getColleaguesByGroup(this.groupId))
    );
  }
}

